import { getPackPreview, usePacks } from "@features/images/use-packs";
import { Heading } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { getMasonrySize, Masonry } from "@components/masonry";
import { PackCategories } from "@components/pack-select";
import { TemplatePackTile } from "@components/template-pack-tile";
import { createCustomTileItem } from "./custom-tile";

type PacksGridProps = {
  searchQuery: string;
  urlProject: string | null;
  urlModel: string | null;
};

export const PacksGrid = ({
  searchQuery,
  urlProject,
  urlModel,
}: PacksGridProps) => {
  const { packs: _packs } = usePacks();
  const packs = _packs.data || [];
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { site } = useParams();

  // Get all category keys
  const categoryKeys = Object.keys(PackCategories);

  // Filter packs based on search query
  const filterPack = (packId: string) => {
    if (!searchQuery) return true;
    const packName = t(["packs." + packId + ".name", packId]).toLowerCase();
    return packName.includes(searchQuery.toLowerCase());
  };

  // Build navigation params
  const buildNavParams = (additionalParams: Record<string, string>) => {
    const params = new URLSearchParams();
    Object.entries(additionalParams).forEach(([key, value]) => {
      params.set(key, value);
    });
    if (urlProject) params.set("project", urlProject);
    if (urlModel) params.set("model", urlModel);
    return params.toString();
  };

  // Custom tile for the first item in each category
  const customTile = createCustomTileItem(
    t("home.custom_prompt"),
    `/camera/new?${buildNavParams({ mode: "classic" })}`,
    764
  );

  return (
    <div className="space-y-8">
      {/* Categories */}
      {categoryKeys.map((categoryKey, index) => {
        const categoryPacks = packs
          .filter((pack) =>
            PackCategories[categoryKey].content.includes(pack.id)
          )
          .filter((pack) => filterPack(pack.id));

        // Don't render empty categories
        if (categoryPacks.length === 0) return null;

        const packItems = categoryPacks.map((pack) => ({
          width: getMasonrySize(pack.size).width || 512,
          height: getMasonrySize(pack.size).height || 512,
          id: `${categoryKey}-${pack.id}`,
          render: (
            <TemplatePackTile
              key={`${categoryKey}-${pack.id}`}
              name={t(["packs." + pack.id + ".name", pack.id])}
              photoCount={pack.count}
              imageUrl={getPackPreview(pack.id)}
              onClick={() => {
                navigate(
                  `/${site}/camera/new?${buildNavParams({
                    mode: "pack",
                    format: "auto",
                    pack: pack.id,
                  })}`
                );
              }}
            />
          ),
        }));

        // Add custom tile as first item in first category when not searching
        const images =
          !searchQuery && index === 0 ? [customTile, ...packItems] : packItems;

        return (
          <div key={categoryKey}>
            <Heading size="4" className="mb-3">
              {t([`packs_categories.${categoryKey}.name`, categoryKey])}
            </Heading>
            <Masonry size="2" className="-mx-1" images={images} />
          </div>
        );
      })}
    </div>
  );
};
